// Mock user data
import type { UserData, AxiosUserResponse } from '@/types/api';
import { mockTenants } from './mockApi/mockTenants';

const mockUserData: UserData = {
  id: 1,
  username: 'admin',
  email: 'admin@risksentinel.com',
  first_name: 'Admin',
  last_name: 'Système',
  is_active: true,
  date_joined: '2023-01-01T00:00:00Z',
  role: 'admin',
  is_staff: true,
  is_superuser: true,
  permissions: ['all']
};

// Mock authentication response
const mockAuthResponse: any = {
  access_token: 'mock_access_token_admin',
  refresh_token: 'mock_refresh_token_admin',
  user: mockUserData
};

// Mock vulnerability scan data
const mockVulnerabilityScans: any[] = [
  {
    id: 1,
    name: 'Scan de vulnérabilité - Serveurs Web',
    description: 'Scan complet des serveurs web pour identifier les vulnérabilités potentielles',
    scan_type: 'web_application',
    scan_status: 'completed',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    end_date: new Date(Date.now() - 29.9 * 24 * 60 * 60 * 1000).toISOString(),
    target_assets: [1, 2],
    target_assets_count: 2,
    configuration: {},
    scanner: 'OWASP ZAP',
    scheduled_time: new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).toISOString(),
    severity_scores: {
      critical_count: 2,
      high_count: 5,
      medium_count: 4,
      low_count: 3,
      created_by: 1,
      updated_at: new Date().toISOString()
    },
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Monthly Infrastructure Scan',
    description: 'Scan mensuel de l\'infrastructure réseau',
    scan_type: 'network',
    scan_status: 'scheduled',
    start_date: null,
    end_date: null,
    target_assets: [3, 4, 5],
    target_assets_count: 3,
    configuration: { scope: 'subnet', ports: '1-1024' },
    scanner: 'Nmap',
    scheduled_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    severity_scores: {
      critical_count: 0,
      high_count: 0,
      medium_count: 0,
      low_count: 0,
      created_by: 1,
      updated_at: new Date().toISOString()
    },
    created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 3,
    name: 'API Security Scan',
    description: 'Scan de sécurité des API',
    scan_type: 'web',
    scan_status: 'in_progress',
    start_date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    end_date: null,
    target_assets: [6],
    target_assets_count: 1,
    configuration: { scope: 'endpoints', authentication: true },
    scanner: 'OWASP API Security',
    scheduled_time: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    severity_scores: {
      critical_count: 0,
      high_count: 0,
      medium_count: 0,
      low_count: 0,
      created_by: 1,
      updated_at: new Date().toISOString()
    },
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 4,
    name: 'Database Security Audit',
    description: 'Audit de sécurité des bases de données',
    scan_type: 'system',
    scan_status: 'failed',
    start_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    end_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    target_assets: [7, 8],
    target_assets_count: 2,
    configuration: { scope: 'full', authentication: true },
    scanner: 'Database Security Scanner',
    scheduled_time: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    severity_scores: {
      critical_count: 0,
      high_count: 0,
      medium_count: 0,
      low_count: 0,
      created_by: 1,
      updated_at: new Date().toISOString()
    },
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 5,
    name: 'Canceled Database Scan',
    description: 'Scan de sécurité des bases de données annulé',
    scan_type: 'system',
    scan_status: 'cancelled',
    start_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    end_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    target_assets: [9],
    target_assets_count: 1,
    configuration: { scope: 'full', authentication: true },
    scanner: 'Database Security Scanner',
    scheduled_time: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    severity_scores: {
      critical_count: 0,
      high_count: 0,
      medium_count: 0,
      low_count: 0,
      created_by: 1,
      updated_at: new Date().toISOString()
    },
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString()
  }
];

// Mock API functions
export const mockAuthAPI = {
  login: async (email: string, password: string) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Simple validation
    if (email && password) {
      return {
        data: mockAuthResponse,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Invalid credentials');
    }
  },
  
  refreshToken: async (refresh: string) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      data: mockAuthResponse,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  getCurrentUser: async () => {
    // Simulate getting the current user, with special handling for admin
    const mockUser: UserData = {
      id: 1,
      username: 'admin',
      email: 'admin@risksentinel.com',
      first_name: 'Admin',
      last_name: 'Système',
      is_active: true,
      date_joined: '2023-01-01T00:00:00Z',
      role: 'admin',
      is_staff: true,
      is_superuser: true,
      permissions: ['all']
    };

    return mockUser;
  },
  
  register: async (userData: {
    username?: string;
    email: string;
    first_name?: string;
    last_name?: string;
    password: string;
  } | Partial<UserData>): Promise<UserData> => {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Validate input
    const email = 'email' in userData ? userData.email : userData.email || '';
    if (!email) {
      throw new Error('Email requis');
    }

    // Generate a username if not provided
    const username = ('username' in userData ? userData.username : userData.username) 
      || email.split('@')[0];

    // Special handling for admin user
    const isAdminUser = email.toLowerCase() === 'admin@risksentinel.com';

    // Mock user data
    const mockUser: UserData = {
      id: isAdminUser ? 1 : Math.floor(Math.random() * 1000) + 1,
      username: username,
      email: email,
      first_name: ('first_name' in userData ? userData.first_name : userData.first_name) 
        || (isAdminUser ? 'Admin' : ''),
      last_name: ('last_name' in userData ? userData.last_name : userData.last_name) 
        || (isAdminUser ? 'Système' : ''),
      is_active: true,
      date_joined: '2023-01-01T00:00:00Z',
      role: isAdminUser ? 'admin' : 'user',
      is_staff: isAdminUser,
      is_superuser: isAdminUser,
      permissions: isAdminUser ? ['all'] : []
    };

    return mockUser;
  },
};

// Helper function to convert UserData to AxiosUserResponse
const fixPromiseType = <T extends UserData>(userData: T): AxiosUserResponse => ({
  data: userData,
  status: 200,
  statusText: 'OK',
  headers: {},
  config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
});

// Mock vulnerability scans API
export const mockVulnerabilityScansAPI = {
  getScans: async (params?: any) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Return mock data
    return {
      data: mockVulnerabilityScans,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  getScan: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const scan = mockVulnerabilityScans.find(scan => scan.id === id);
    
    if (scan) {
      return {
        data: scan,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Scan not found');
    }
  },
  
  createScan: async (data: any) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Create a new scan with the provided data
    const newScan: any = {
      id: Math.max(...mockVulnerabilityScans.map(s => s.id)) + 1,
      name: data.name || 'Nouveau scan de vulnérabilité',
      description: data.description || 'Scan de vulnérabilité généré automatiquement',
      scan_type: (data.scan_type || 'web_application'),
      scan_status: (data.scan_status || 'pending'),
      start_date: data.start_date || new Date().toISOString(),
      end_date: data.end_date || null,
      target_assets: data.target_assets || [],
      target_assets_count: data.target_assets_count || 0,
      configuration: data.configuration || {},
      scanner: data.scanner || 'Système automatique',
      scheduled_time: data.scheduled_time || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default to 24 hours from now
      severity_scores: {
        critical_count: 0,
        high_count: 0,
        medium_count: 0,
        low_count: 0,
        created_by: 1,
        updated_at: new Date().toISOString()
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // Add to mock data
    mockVulnerabilityScans.push(newScan);
    
    return {
      data: newScan,
      status: 201,
      statusText: 'Created',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  updateScan: async (id: number, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const index = mockVulnerabilityScans.findIndex(scan => scan.id === id);
    if (index !== -1) {
      // Update the scan
      const updatedScan = {
        ...mockVulnerabilityScans[index],
        ...data,
        updated_at: new Date().toISOString()
      };
      
      mockVulnerabilityScans[index] = updatedScan;
      
      return {
        data: updatedScan,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Scan not found');
    }
  },
  
  deleteScan: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 700));
    
    const index = mockVulnerabilityScans.findIndex(scan => scan.id === id);
    if (index !== -1) {
      // Remove the scan
      mockVulnerabilityScans.splice(index, 1);
      
      return {
        data: { success: true },
        status: 204,
        statusText: 'No Content',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Scan not found');
    }
  },
  
  startScan: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const index = mockVulnerabilityScans.findIndex(scan => scan.id === id);
    if (index !== -1) {
      // Update the scan status
      mockVulnerabilityScans[index] = {
        ...mockVulnerabilityScans[index],
        scan_status: 'in_progress',
        start_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      return {
        data: { success: true },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Scan not found');
    }
  },
  
  cancelScan: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const index = mockVulnerabilityScans.findIndex(scan => scan.id === id);
    if (index !== -1) {
      // Update the scan status
      mockVulnerabilityScans[index] = {
        ...mockVulnerabilityScans[index],
        scan_status: 'cancelled',
        updated_at: new Date().toISOString()
      };
      
      return {
        data: { success: true },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Scan not found');
    }
  },
  
  getUpcomingScans: async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Filter scheduled scans
    const upcomingScans = mockVulnerabilityScans.filter(scan => 
      scan.scan_status === 'scheduled'
    );
    
    return {
      data: upcomingScans,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  }
};

// Mock threat intelligence data
const mockThreats: any[] = [
  {
    id: "TH-001",
    title: "Emotet Malware Campaign",
    description: "Sophisticated banking trojan spreading through email phishing",
    severity: 'high',
    type: 'malware',
    source: 'Threat Intelligence Feed',
    tags: ['malware', 'banking', 'phishing'],
    first_seen: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    confidence_score: 0.85,
    indicators: [
      {
        id: 'IND-001',
        type: 'ip_address',
        value: '192.168.1.100',
        confidence: 0.9,
        description: 'Known Emotet C2 server',
        source: 'Threat Feed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ]
  },
  {
    id: "TH-002",
    title: "Trickbot Banking Trojan",
    description: "Targeted banking trojan with advanced evasion techniques",
    severity: 'critical',
    type: 'malware',
    source: 'MISP',
    tags: ['trickbot', 'banking', 'trojan'],
    first_seen: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    confidence_score: 0.9,
    indicators: [
      {
        id: 'IND-002',
        type: 'domain',
        value: 'trickbot-c2.net',
        confidence: 0.9,
        description: 'Trickbot C2 domain',
        source: 'MISP',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ]
  },
  {
    id: "TH-003",
    title: "Ransomware Attack Campaign",
    description: "Targeted ransomware campaign affecting multiple industries",
    severity: 'critical',
    type: 'ransomware',
    source: 'Manual',
    tags: ['ransomware', 'targeted', 'attack'],
    first_seen: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    confidence_score: 0.95,
    indicators: [
      {
        id: 'IND-003',
        type: 'file_hash',
        value: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z',
        confidence: 0.95,
        description: 'Ransomware payload hash',
        source: 'Manual',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ]
  }
];

// Mock threat summary data
const mockThreatSummary: any = {
  total_threats: 15,
  critical_threats: 4,
  sources: 3,
  indicators: 42,
  by_severity: {
    critical: 4,
    high: 6,
    medium: 3,
    low: 2
  },
  by_source: {
    "TAXII": 8,
    "MISP": 5,
    "Manual": 2
  },
  by_type: {
    "Malware": 7,
    "Phishing": 3,
    "Ransomware": 2,
    "Vulnerability": 3
  },
  trend_data: [
    {
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      count: 2,
      by_type: { "Malware": 1, "Phishing": 1 }
    },
    {
      date: new Date(Date.now() - 23 * 24 * 60 * 60 * 1000).toISOString(),
      count: 3,
      by_type: { "Malware": 2, "Vulnerability": 1 }
    },
    {
      date: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(),
      count: 4,
      by_type: { "Malware": 1, "Ransomware": 1, "Phishing": 1, "Vulnerability": 1 }
    },
    {
      date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
      count: 3,
      by_type: { "Malware": 2, "Phishing": 1 }
    },
    {
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      count: 3,
      by_type: { "Malware": 1, "Ransomware": 1, "Vulnerability": 1 }
    }
  ],
  geo_distribution: {
    "US": 5,
    "RU": 3,
    "CN": 2,
    "FR": 2,
    "DE": 1,
    "UK": 1,
    "BR": 1
  }
};

// Mock threats API
export const mockThreatsAPI = {
  getThreats: async (params?: any) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    let filteredThreats = [...mockThreats];
    
    // Apply filters if provided
    if (params) {
      if (params.severity) {
        filteredThreats = filteredThreats.filter(threat => 
          threat.severity === params.severity
        );
      }
      
      if (params.type) {
        filteredThreats = filteredThreats.filter(threat => 
          threat.type === params.type
        );
      }
      
      if (params.source) {
        filteredThreats = filteredThreats.filter(threat => 
          threat.source === params.source
        );
      }
    }
    
    return {
      data: filteredThreats,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  getThreat: async (id: string) => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const threat = mockThreats.find(t => t.id === id);
    
    if (threat) {
      return {
        data: threat,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Threat not found');
    }
  },
  
  getThreatSummary: async () => {
    await new Promise(resolve => setTimeout(resolve, 700));
    
    return {
      data: mockThreatSummary,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  createThreat: async (data: any) => {
    await new Promise(resolve => setTimeout(resolve, 900));
    
    const newId = `TH-${String(mockThreats.length + 1).padStart(3, '0')}`;
    
    const newThreat: any = {
      id: newId,
      title: data.title || 'New Threat',
      description: data.description || '',
      source: data.source || 'Manual',
      source_url: data.source_url,
      type: data.type || 'Unknown',
      severity: data.severity || 'medium',
      confidence_score: data.confidence_score || 50,
      tlp: data.tlp || 'amber',
      first_seen: data.first_seen || new Date().toISOString(),
      last_updated: new Date().toISOString(),
      tags: data.tags || [],
      related_threats: data.related_threats || [],
      affected_assets: data.affected_assets || [],
      mitigations: data.mitigations || [],
      indicators: (data as any).indicators || [],
      updated_at: new Date().toISOString()
    };
    
    mockThreats.push(newThreat);
    
    return {
      data: newThreat,
      status: 201,
      statusText: 'Created',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  updateThreat: async (id: string, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const index = mockThreats.findIndex(threat => threat.id === id);
    
    if (index !== -1) {
      // Utiliser un type casting pour éviter les erreurs TypeScript
      const updatedThreat = {
        ...mockThreats[index],
        ...data,
        updated_at: new Date().toISOString()
      };
      
      mockThreats[index] = updatedThreat as any;
      
      return {
        data: updatedThreat,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Threat not found');
    }
  },
  
  deleteThreat: async (id: string) => {
    await new Promise(resolve => setTimeout(resolve, 700));
    
    const index = mockThreats.findIndex(threat => threat.id === id);
    
    if (index !== -1) {
      mockThreats.splice(index, 1);
      
      return {
        data: { success: true },
        status: 204,
        statusText: 'No Content',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Threat not found');
    }
  },
  
  exportThreats: async (format: 'csv' | 'json' | 'stix', params?: any) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In a real implementation, this would return a blob with the exported data
    return {
      data: new Blob(['Mock export data'], { type: 'text/plain' }),
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  }
};

// Mock assets data
const mockAssets: any[] = [
  {
    id: 1,
    name: 'Serveur Web Principal',
    asset_type: 'server',
    description: 'Serveur web principal hébergeant les applications critiques',
    owner: 1,
    criticality: 'high',
    location: 'Datacenter Principal',
    ip_address: '192.168.1.100',
    mac_address: '00:11:22:33:44:55',
    purchase_date: '2023-01-15',
    last_updated: '2025-03-10T14:30:00Z',
    created_at: '2023-01-20T10:00:00Z',
    configuration: {
      os: 'Ubuntu 20.04 LTS',
      kernel_version: '5.4.0-42-generic'
    },
    tags: ['production', 'web'],
    vulnerabilities_count: {
      critical: 2,
      high: 5,
      medium: 10,
      low: 15
    }
  },
  {
    id: 2,
    name: 'Base de données clients',
    asset_type: 'database',
    description: 'Base de données principale contenant les informations clients',
    owner: 2,
    criticality: 'critical',
    location: 'Datacenter Paris',
    ip_address: '192.168.1.20',
    mac_address: '00:1A:2B:3C:4D:6F',
    purchase_date: '2023-01-15',
    last_updated: '2025-03-15T09:45:00Z',
    created_at: '2023-01-20T10:30:00Z'
  },
  {
    id: 3,
    name: 'Poste de travail - Direction',
    asset_type: 'workstation',
    description: 'Ordinateur portable du directeur général',
    owner: 3,
    criticality: 'high',
    location: 'Bureau Direction',
    ip_address: '192.168.2.15',
    mac_address: '00:2C:3D:4E:5F:6G',
    purchase_date: '2024-02-10',
    last_updated: '2025-03-20T11:20:00Z',
    created_at: '2024-02-15T14:00:00Z'
  },
  {
    id: 4,
    name: 'Firewall principal',
    asset_type: 'network_device',
    description: 'Firewall protégeant le réseau interne',
    owner: 1,
    criticality: 'critical',
    location: 'Salle serveur',
    ip_address: '192.168.0.1',
    mac_address: '00:3D:4E:5F:6G:7H',
    purchase_date: '2022-11-05',
    last_updated: '2025-02-28T16:40:00Z',
    created_at: '2022-11-10T09:15:00Z'
  },
  {
    id: 5,
    name: 'Application CRM',
    asset_type: 'application',
    description: 'Application de gestion de la relation client',
    owner: 2,
    criticality: 'medium',
    location: 'Cloud',
    ip_address: null,
    mac_address: 'N/A',
    purchase_date: '2023-06-20',
    last_updated: '2025-03-25T10:10:00Z',
    created_at: '2023-06-25T13:45:00Z'
  },
  {
    id: 6,
    name: 'Service AWS S3',
    asset_type: 'cloud_service',
    description: 'Stockage de fichiers dans le cloud AWS',
    owner: 1,
    criticality: 'medium',
    location: 'AWS eu-west-3',
    ip_address: null,
    mac_address: 'N/A',
    purchase_date: null,
    last_updated: '2025-03-18T08:30:00Z',
    created_at: '2023-05-12T11:20:00Z'
  }
];

// Conversion des types numériques en chaînes de caractères
const convertToString = (value: string | number | null): string | null => {
  if (value === null) return null;
  return String(value);
};

// Mise à jour des fonctions pour gérer les conversions de types
export const mockAssetsAPI = {
  getAssets: async (params?: any) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Filter assets based on params if needed
    let filteredAssets = [...mockAssets];
    
    if (params) {
      if (params.search) {
        const searchLower = params.search.toLowerCase();
        filteredAssets = filteredAssets.filter(asset => 
          asset.name.toLowerCase().includes(searchLower) || 
          asset.description.toLowerCase().includes(searchLower)
        );
      }
      
      if (params.asset_type) {
        const types = params.asset_type.split(',');
        filteredAssets = filteredAssets.filter(asset => types.includes(asset.asset_type));
      }
      
      if (params.criticality) {
        const criticalities = params.criticality.split(',');
        filteredAssets = filteredAssets.filter(asset => criticalities.includes(asset.criticality));
      }
    }
    
    return {
      data: filteredAssets,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  },
  
  getAsset: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const asset = mockAssets.find(a => a.id === id);
    
    if (asset) {
      return {
        data: asset,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Asset not found');
    }
  },
  
  createAsset: async (data: any): Promise<any> => {
    const newAsset: any = {
      id: data.id || mockAssets.length + 1,
      name: data.name || 'Nouvel Asset',
      description: data.description || 'Description non spécifiée',
      type: data.type || 'server',
      criticality: data.criticality || 'low',
      ip_address: convertToString(data.ip_address) || 'N/A',
      mac_address: convertToString(data.mac_address) || 'N/A',
      location: data.location || 'Non spécifié',
      owner: data.owner || 1,
      department: data.department || 'Non spécifié',
      status: data.status || 'active',
      last_scanned: data.last_scanned || new Date().toISOString(),
      last_updated: data.last_updated || new Date().toISOString(),
      created_at: data.created_at || new Date().toISOString(),
      configuration: data.configuration || {},
      tags: data.tags || [],
      vulnerabilities_count: data.vulnerabilities_count || {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0
      }
    };
    
    mockAssets.push(newAsset);
    return newAsset;
  },
  
  updateAsset: async (id: number, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 700));
    
    const index = mockAssets.findIndex(a => a.id === id);
    
    if (index !== -1) {
      const updatedAsset = {
        ...mockAssets[index],
        ...data,
        last_updated: new Date().toISOString()
      };
      
      mockAssets[index] = updatedAsset;
      
      return {
        data: updatedAsset,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Asset not found');
    }
  },
  
  deleteAsset: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const index = mockAssets.findIndex(a => a.id === id);
    
    if (index !== -1) {
      mockAssets.splice(index, 1);
      
      return {
        data: { success: true },
        status: 204,
        statusText: 'No Content',
        headers: {},
        config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
      };
    } else {
      throw new Error('Asset not found');
    }
  },
  
  getAssetDependencies: async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock dependencies data
    const dependencies = [
      {
        id: 1,
        source_asset: mockAssets.find(a => a.id === id),
        target_asset: mockAssets.find(a => a.id !== id),
        dependency_type: 'depends_on',
        description: 'Dépendance automatiquement générée',
        created_at: '2025-01-15T10:00:00Z'
      }
    ];
    
    return {
      data: dependencies,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: { headers: (typeof window !== 'undefined' && (window as any).AxiosHeaders) ? new (window as any).AxiosHeaders() : {} }
    };
  }
};

// Mock API v1
export const mockApiV1 = {
  health: async () => {
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate network delay
    return {
      status: 'ok',
      timestamp: new Date().toISOString(),
      services: {
        database: 'connected',
        authentication: 'operational',
        threat_intelligence: 'operational',
        vulnerability_scanner: 'operational'
      },
      version: '1.0.0',
      environment: 'development'
    };
  },
  threats: {
    getThreatSummary: async () => {
      await new Promise(resolve => setTimeout(resolve, 700));
      return {
        status: 'ok',
        timestamp: new Date().toISOString(),
        data: {
          total_threats: 42,
          severity_distribution: {
            low: 20,
            medium: 15,
            high: 5,
            critical: 2
          },
          top_threat_sources: [
            { name: 'External Network', count: 15 },
            { name: 'Phishing', count: 12 },
            { name: 'Malware', count: 8 },
            { name: 'Insider Threat', count: 5 },
            { name: 'Misconfiguration', count: 2 }
          ],
          recent_trends: [
            { type: 'Ransomware', count: 3 },
            { type: 'Data Breach', count: 2 },
            { type: 'Social Engineering', count: 1 }
          ]
        }
      };
    },
    getRecentThreats: async () => {
      await new Promise(resolve => setTimeout(resolve, 800));
      return {
        status: 'ok',
        timestamp: new Date().toISOString(),
        data: {
          results: [
            {
              id: crypto.randomUUID(),
              title: 'Potential Phishing Attempt',
              description: 'Suspicious email detected from unknown sender',
              severity: 'medium',
              source: 'Email Gateway',
              type: 'Phishing',
              created_at: new Date().toISOString()
            },
            {
              id: crypto.randomUUID(),
              title: 'Unauthorized Access Attempt',
              description: 'Multiple failed login attempts detected',
              severity: 'high',
              source: 'Authentication System',
              type: 'Intrusion',
              created_at: new Date().toISOString()
            },
            {
              id: crypto.randomUUID(),
              title: 'Potential Malware Infection',
              description: 'Suspicious file detected in network downloads',
              severity: 'critical',
              source: 'Endpoint Protection',
              type: 'Malware',
              created_at: new Date().toISOString()
            }
          ]
        }
      };
    }
  }
};

export const api = {
  getCurrentUser: async (): Promise<UserData> => {
    return {
      id: 1,
      username: 'admin',
      email: 'admin@example.com',
      role: 'admin',
      is_active: true,
      date_joined: new Date().toISOString(),
      first_name: 'Admin',
      last_name: 'User',
      is_staff: true,
      is_superuser: true
    };
  },
  
  login: async (email: string, password: string): Promise<UserData> => {
    return {
      id: 1,
      username: 'admin',
      email: 'admin@example.com',
      role: 'admin',
      is_active: true,
      date_joined: new Date().toISOString(),
      first_name: 'Admin',
      last_name: 'User',
      is_staff: true,
      is_superuser: true
    };
  },
  
  register: async (userData: any): Promise<UserData> => {
    return {
      id: 2,
      username: userData.username || '',
      email: userData.email || '',
      role: 'user',
      is_active: false,
      date_joined: new Date().toISOString(),
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      is_staff: false,
      is_superuser: false
    };
  }
};

// Exporter un objet par défaut pour résoudre les erreurs d'import
const mockApi = {
  mockUserData,
  mockAuthResponse,
  mockVulnerabilityScans,
  mockAssets,
  mockAssetsAPI,
  mockThreatsAPI,
  mockApiV1,
  mockTenants,
  api,
  // Ajouter des alias pour la compatibilité
  assetsAPI: mockAssetsAPI,
  threatsAPI: mockThreatsAPI,
  apiV1: mockApiV1
};

// Export des API individuelles pour la compatibilité avec le code existant
export {
  mockUserData,
  mockAuthResponse,
  mockVulnerabilityScans,
  mockAssets,
  mockTenants
};

// Export par défaut pour résoudre les erreurs d'import
export default mockApi;

import { 
  AuthResponse, 
  VulnerabilityScan, 
  ScanType, 
  ScanStatus, 
  Asset, 
  AssetType, 
  CriticalityLevel,
  ThreatIntelligence,
  ThreatSummary
} from '@/types/api';

import { User } from '@/types/user';
